/**
 * The moderation and member dialogs (user-management §4.5, §7.2 C8): time
 * out, kick, ban (duration and message purge), nickname, poke, move, the
 * ownership transfer, and the step-up dialog the moderator brake and
 * destructive actions open. Each says what will happen before it happens,
 * reports success only after the server answered, and keeps the dialog open
 * with the reason when it is refused.
 */
import {
  ApiErrorCode,
  parseTwoStepCode,
  type Snowflake,
  type StepUpRequest,
} from '@neosocial/protocol';
import { useState } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { api, isApiRequestError } from '../lib/api';
import { useData, useDataShallow } from '../store/data';
import { displayName, userName, voiceStatesIn } from '../store/selectors';
import { Avatar, Btn, Icon } from '../ui/kit';
import { Dialog, toast } from '../ui/overlay';
import { errorText, runAction, useStepUp } from './actions';
import { moveMemberFromUi } from '../hub/voiceUi';
import { moveTargets } from './perms';

export type ModDialogKind = 'timeout' | 'kick' | 'ban' | 'nick' | 'poke' | 'move' | 'transfer';

export interface ModDialogState {
  kind: ModDialogKind;
  guildId: Snowflake;
  userId: Snowflake;
}

export const useModDialog = create<{ open: ModDialogState | null }>(() => ({ open: null }));

export function openModDialog(d: ModDialogState): void {
  useModDialog.setState({ open: d });
}

function close(): void {
  useModDialog.setState({ open: null });
}

/** Timeout lengths in seconds (§4.10 `mod.timeout.for`). */
export const TIMEOUT_CHOICES = [
  { key: 'um.dur.60s', s: 60 },
  { key: 'um.dur.5m', s: 300 },
  { key: 'um.dur.10m', s: 600 },
  { key: 'um.dur.1h', s: 3600 },
  { key: 'um.dur.1d', s: 86_400 },
  { key: 'um.dur.1w', s: 604_800 },
] as const;
export const TIMEOUT_MAX_DAYS = 28;

/** Ban lengths (§4.10 `mod.ban.for`); null is permanent. */
export const BAN_CHOICES = [
  { key: 'um.dur.1h', s: 3600 },
  { key: 'um.dur.1d', s: 86_400 },
  { key: 'um.ban.7d', s: 604_800 },
  { key: 'um.ban.30d', s: 2_592_000 },
  { key: 'um.ban.perm', s: null },
] as const;

/** "Delete their messages from" (§4.10 `mod.ban.delete`), as delete_message_seconds. */
export const BAN_DELETE_CHOICES = [
  { key: 'um.ban.del.none', s: 0 },
  { key: 'um.ban.del.1h', s: 3600 },
  { key: 'um.ban.del.6h', s: 21_600 },
  { key: 'um.ban.del.12h', s: 43_200 },
  { key: 'um.ban.del.24h', s: 86_400 },
  { key: 'um.ban.del.3d', s: 259_200 },
  { key: 'um.ban.del.7d', s: 604_800 },
] as const;

/** The timeout end for a choice; custom days are clamped to 1-28. */
export function timeoutUntil(seconds: number | null, customDays: number, now = Date.now()): Date {
  const s =
    seconds ?? Math.max(1, Math.min(TIMEOUT_MAX_DAYS, Math.round(customDays || 1))) * 86_400;
  return new Date(now + s * 1000);
}

function useName(guildId: Snowflake, userId: Snowflake): string {
  return useData((d) => userName(d, userId, guildId));
}

function Target({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const user = useData((d) => d.users[userId]);
  const name = useName(guildId, userId);
  return (
    <div className="ns-mod-target">
      <Avatar user={user} name={name} size={32} />
      <b dir="auto">{name}</b>
      {user ? (
        <span className="ns-muted" dir="ltr">
          {user.username}
        </span>
      ) : null}
    </div>
  );
}

function Reason({ value, onChange }: { value: string; onChange(v: string): void }) {
  const { t } = useT();
  return (
    <label className="ns-field">
      <span className="ns-label">{t('mod.reason')}</span>
      <textarea
        className="nx-textarea"
        dir="auto"
        rows={2}
        maxLength={512}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Chips({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ key: string; s: number | null }>;
  value: number | null;
  onChange(v: number | null): void;
}) {
  const { t } = useT();
  return (
    <fieldset className="ns-field ns-chips">
      <legend className="ns-label">{label}</legend>
      <div className="ns-chips-row" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            role="radio"
            className="nx-chipbtn"
            aria-checked={value === o.s}
            aria-pressed={value === o.s}
            onClick={() => onChange(o.s)}
          >
            {t(o.key)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function ErrorLine({ text }: { text: string | null }) {
  return text ? (
    <p className="ns-err" role="alert">
      {text}
    </p>
  ) : null;
}

/* ---------------- time out ---------------- */

function TimeoutDialog({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const { t, fmt } = useT();
  const name = useName(guildId, userId);
  const [secs, setSecs] = useState<number | null>(600);
  const [custom, setCustom] = useState(false);
  const [days, setDays] = useState(2);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const until = timeoutUntil(custom ? null : secs, days);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    const r = await runAction(
      () =>
        api.um.modifyMember(
          guildId,
          userId,
          { communication_disabled_until: until.toISOString() },
          reason.trim() || undefined,
        ),
      { quiet: true },
    );
    setBusy(false);
    if (r.ok) {
      toast(t('um.timeout.done', { name, time: fmt.stamp(until) }), { icon: 'clock', tone: 'ok' });
      close();
    } else if (!isApiRequestError(r.error, 100001)) setErr(errorText(r.error));
  };
  return (
    <Dialog
      title={t('um.timeout.title', { name })}
      icon="clock"
      tone="mode"
      width={480}
      onClose={close}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={close}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="primary" size="sm" busy={busy} onClick={() => void submit()}>
            {t('mod.timeout')}
          </Btn>
        </>
      }
    >
      <Target guildId={guildId} userId={userId} />
      <p>{t('um.timeout.body')}</p>
      <fieldset className="ns-field ns-chips">
        <legend className="ns-label">{t('um.timeout.for')}</legend>
        <div className="ns-chips-row" role="radiogroup" aria-label={t('um.timeout.for')}>
          {TIMEOUT_CHOICES.map((o) => (
            <button
              key={o.key}
              type="button"
              role="radio"
              className="nx-chipbtn"
              aria-checked={!custom && secs === o.s}
              aria-pressed={!custom && secs === o.s}
              onClick={() => {
                setCustom(false);
                setSecs(o.s);
              }}
            >
              {t(o.key)}
            </button>
          ))}
          <button
            type="button"
            role="radio"
            className="nx-chipbtn"
            aria-checked={custom}
            aria-pressed={custom}
            onClick={() => setCustom(true)}
          >
            {t('um.dur.custom')}
          </button>
        </div>
      </fieldset>
      {custom ? (
        <label className="ns-field">
          <span className="ns-label">{t('um.dur.customDays')}</span>
          <input
            className="nx-input ns-num-in"
            type="number"
            min={1}
            max={TIMEOUT_MAX_DAYS}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          />
        </label>
      ) : null}
      <p className="ns-muted">{t('um.timeout.until', { time: fmt.stamp(until) })}</p>
      <Reason value={reason} onChange={setReason} />
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- kick ---------------- */

function KickDialog({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const { t } = useT();
  const name = useName(guildId, userId);
  const server = useData((d) => d.guilds[guildId]?.name ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    const r = await runAction(() => api.um.kick(guildId, userId, reason.trim() || undefined), {
      quiet: true,
    });
    setBusy(false);
    if (r.ok) {
      toast(t('um.kick.done', { name }), { icon: 'arrow-out', tone: 'ok' });
      close();
    } else if (!isApiRequestError(r.error, 100001)) setErr(errorText(r.error));
  };
  return (
    <Dialog
      title={t('um.kick.title', { name })}
      icon="arrow-out"
      tone="danger"
      width={460}
      onClose={close}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={close}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="danger" size="sm" busy={busy} onClick={() => void submit()}>
            {t('mod.kick')}
          </Btn>
        </>
      }
    >
      <Target guildId={guildId} userId={userId} />
      <p>{t('um.kick.body', { server })}</p>
      <Reason value={reason} onChange={setReason} />
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- ban ---------------- */

function BanDialog({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const { t } = useT();
  const name = useName(guildId, userId);
  const [len, setLen] = useState<number | null>(86_400);
  const [del, setDel] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    const expires = len === null ? null : new Date(Date.now() + len * 1000).toISOString();
    const r = await runAction(
      () =>
        api.um.ban(
          guildId,
          userId,
          { delete_message_seconds: del, nx_expires_at: expires },
          reason.trim() || undefined,
        ),
      { quiet: true },
    );
    setBusy(false);
    if (r.ok) {
      toast(t('um.ban.done', { name }), { icon: 'cm-ban', tone: 'ok' });
      close();
    } else if (!isApiRequestError(r.error, 100001)) setErr(errorText(r.error));
  };
  return (
    <Dialog
      title={t('um.ban.title', { name })}
      icon="cm-ban"
      tone="danger"
      width={520}
      onClose={close}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={close}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="danger" size="sm" busy={busy} onClick={() => void submit()}>
            {t('mod.ban')}
          </Btn>
        </>
      }
    >
      <Target guildId={guildId} userId={userId} />
      <Chips label={t('um.ban.for')} options={BAN_CHOICES} value={len} onChange={setLen} />
      <Chips
        label={t('um.ban.delete')}
        options={BAN_DELETE_CHOICES}
        value={del}
        onChange={(v) => setDel(v ?? 0)}
      />
      <Reason value={reason} onChange={setReason} />
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- nickname ---------------- */

function NickDialog({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const { t } = useT();
  const meId = useData((d) => d.me?.id);
  const member = useData((d) => d.members[guildId]?.[userId]);
  const user = useData((d) => d.users[userId]);
  const server = useData((d) => d.guilds[guildId]?.name ?? '');
  const [nick, setNick] = useState(member?.nick ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mine = userId === meId;
  const name = displayName(user);
  const save = async (value: string | null) => {
    setBusy(true);
    setErr(null);
    const body = { nick: value && value.trim() ? value.trim() : null };
    const r = await runAction(
      () => (mine ? api.um.modifyMe(guildId, body) : api.um.modifyMember(guildId, userId, body)),
      { quiet: true },
    );
    setBusy(false);
    if (r.ok) {
      toast(t(body.nick ? 'um.nick.saved' : 'um.nick.cleared'), { icon: 'check', tone: 'ok' });
      close();
    } else if (!isApiRequestError(r.error, 100001)) setErr(errorText(r.error));
  };
  return (
    <Dialog
      title={mine ? t('um.nick.titleMine', { server }) : t('um.nick.title', { name })}
      icon="edit"
      tone="mode"
      width={440}
      onClose={close}
      actions={
        <>
          {member?.nick ? (
            <Btn kind="ghost" size="sm" disabled={busy} onClick={() => void save(null)}>
              {t('um.nick.reset')}
            </Btn>
          ) : null}
          <Btn kind="ghost" size="sm" onClick={close}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="primary" size="sm" busy={busy} onClick={() => void save(nick)}>
            {t('c.save')}
          </Btn>
        </>
      }
    >
      <form
        className="ns-field"
        onSubmit={(e) => {
          e.preventDefault();
          void save(nick);
        }}
      >
        <label>
          <span className="ns-label">{t('um.nick.label')}</span>
          <input
            className="nx-input"
            dir="auto"
            maxLength={32}
            value={nick}
            placeholder={name}
            data-autofocus
            onChange={(e) => setNick(e.target.value)}
          />
        </label>
        <span className="ns-help">{t('um.nick.help')}</span>
      </form>
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- poke (prototype M.pokeDialog) ---------------- */

const POKE_MAX = 100;

function PokeDialog({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const { t } = useT();
  const name = useName(guildId, userId);
  const user = useData((d) => d.users[userId]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setErr(null);
    const r = await runAction(
      () => api.um.poke(userId, text.trim() ? { message: text.trim() } : {}),
      { quiet: true },
    );
    setBusy(false);
    if (r.ok) {
      toast(t('ns.poke.sent', { who: name }), { icon: 'zap' });
      close();
    } else if (
      // the server answers every refusal (block, "Pokes from", unreachable) the same way
      isApiRequestError(r.error, ApiErrorCode.POKE_FAILED) ||
      isApiRequestError(r.error, 50013) ||
      isApiRequestError(r.error, 50007)
    )
      setErr(t('um.poke.disallowed', { name }));
    else setErr(errorText(r.error));
  };
  return (
    <Dialog
      title={t('ns.poke.title', { name })}
      icon="zap"
      tone="mode"
      width={440}
      onClose={close}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={close}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="primary" size="sm" icon="zap" busy={busy} onClick={() => void send()}>
            {t('ns.poke.send')}
          </Btn>
        </>
      }
    >
      <div className="ns-poke">
        <div className="ns-poke-to">
          <Avatar user={user} name={name} size={28} />
          <b dir="auto">{name}</b>
        </div>
        <label className="ns-field">
          <span className="ns-label">{t('ns.poke.label')}</span>
          <input
            type="text"
            className="nx-input ns-poke-in"
            dir="auto"
            maxLength={POKE_MAX}
            placeholder={t('ns.poke.ph')}
            value={text}
            data-autofocus
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void send();
              }
            }}
          />
        </label>
        <div className="ns-poke-quick">
          {(['ns.poke.q1', 'ns.poke.q2', 'ns.poke.q3'] as const).map((k) => (
            <button key={k} type="button" className="nx-chipbtn" onClick={() => setText(t(k))}>
              {t(k)}
            </button>
          ))}
        </div>
        <p className="ns-muted">{t('ns.poke.left', { n: POKE_MAX - text.length })}</p>
      </div>
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- move ---------------- */

function MoveDialog({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const { t, fmt } = useT();
  const name = useName(guildId, userId);
  const targets = useDataShallow((d) => moveTargets(d, guildId, userId));
  const channels = useData((d) => d.channels);
  const current = useData((d) => d.voiceStates[guildId]?.[userId]?.channel_id ?? null);
  const counts = useDataShallow((d) => targets.map((id) => voiceStatesIn(d, guildId, id).length));
  const [busy, setBusy] = useState<Snowflake | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // the same move as the sidebar's drag and drop (`moveMemberFromUi`): you,
  // connected here, switch in the voice engine; anyone else, the server
  const move = async (id: Snowflake) => {
    setBusy(id);
    setErr(null);
    const r = await moveMemberFromUi(guildId, userId, id, { quiet: true });
    setBusy(null);
    if (r.ok) {
      if (r.route === 'server')
        toast(t('um.move.done', { name, channel: channels[id]?.name ?? '' }), {
          icon: 'cm-move',
          tone: 'ok',
        });
      close();
    } else if (r.error !== undefined && !isApiRequestError(r.error, 100001))
      setErr(errorText(r.error));
  };
  return (
    <Dialog
      title={t('um.move.title', { name })}
      icon="cm-move"
      tone="mode"
      width={420}
      onClose={close}
    >
      <ul className="ns-move-list">
        {targets.map((id, i) => {
          const c = channels[id];
          const here = id === current;
          return (
            <li key={id}>
              <button
                type="button"
                className="ns-adm-item"
                disabled={here || !!busy}
                aria-current={here ? 'true' : undefined}
                onClick={() => void move(id)}
              >
                {busy === id ? <i className="nx-spin" /> : <Icon name="volume" />}
                {/* the line's own direction (a Latin name sat at the far left of
                    a right-to-left line, beside the count); the name itself isolated */}
                <span>
                  <bdi>{c?.name}</bdi>
                </span>
                <span className="ns-muted nx-num">
                  {here ? t('um.move.here') : fmt.num(counts[i] ?? 0)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- ownership transfer ---------------- */

function TransferDialog({ guildId, userId }: { guildId: Snowflake; userId: Snowflake }) {
  const { t } = useT();
  const name = useName(guildId, userId);
  const server = useData((d) => d.guilds[guildId]?.name ?? '');
  const mfa = useData((d) => !!d.me?.mfa_enabled);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    // Takes effect at once; the server asks an owner with two-step for a code first.
    const r = await runAction(() => api.um.startOwnerTransfer(guildId, { user_id: userId }), {
      quiet: true,
      challenge: { reason: 'account', level: 'second_factor' },
    });
    setBusy(false);
    if (r.ok) {
      toast(t('um.owner.done', { name, server }), {
        icon: 'crown',
        tone: 'ok',
      });
      close();
    } else if (!isApiRequestError(r.error, 100001)) setErr(errorText(r.error));
  };
  return (
    <Dialog
      title={t('owner.transfer')}
      icon="crown"
      tone="danger"
      width={480}
      onClose={close}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={close}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="danger" size="sm" busy={busy} onClick={() => void submit()}>
            {t('um.owner.start')}
          </Btn>
        </>
      }
    >
      <Target guildId={guildId} userId={userId} />
      <p>{t('owner.transfer.body', { name, server })}</p>
      {mfa ? <p className="ns-muted">{t('owner.transfer.code')}</p> : null}
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- step-up (brake, destructive, account) ---------------- */

/**
 * What the step-up sends for what was typed. With two-step on, the code
 * field takes either kind, as its text says ("…from your authenticator app,
 * or one of your recovery codes"): told apart by form (6 or 8 digits, the
 * console's rule), so a recovery code works without "Use a recovery code
 * instead" first. That link still makes the field a recovery-code field.
 */
export function stepUpBody(
  value: string,
  o: { useCode: boolean; recovery: boolean },
): StepUpRequest {
  if (!o.useCode) return { password: value };
  if (o.recovery) return { recovery_code: value.trim() };
  const typed = parseTwoStepCode(value);
  return typed?.kind === 'recovery'
    ? { recovery_code: typed.code }
    : { totp_code: value.replace(/\s/g, '') };
}

export function StepUpDialog() {
  const { t } = useT();
  const ask = useStepUp((s) => s.ask);
  const mfa = useData((d) => !!d.me?.mfa_enabled);
  const [value, setValue] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!ask) return null;
  const useCode = ask.challenge.level === 'second_factor' && mfa;
  const done = (ok: boolean) => {
    useStepUp.setState({ ask: null });
    setValue('');
    setErr(null);
    setRecovery(false);
    ask.resolve(ok);
  };
  const submit = async () => {
    if (!value.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.auth.stepUp(stepUpBody(value, { useCode, recovery }));
      setBusy(false);
      done(true);
    } catch (e) {
      setBusy(false);
      setErr(stepUpError(e));
    }
  };
  /** Too many tries is said as such (with the wait), never as a wrong password. */
  const stepUpError = (e: unknown): string => {
    if (!isApiRequestError(e)) return errorText(e);
    if (e.status === 429) {
      const s = Math.max(1, Math.ceil(e.retryAfter ?? 0));
      if (!e.retryAfter) return t('ns.err.429');
      return s > 90
        ? t('um.stepUp.waitMin', { n: Math.ceil(s / 60) })
        : t('um.stepUp.wait', { n: s });
    }
    return e.status < 500 ? t('um.stepUp.wrong') : errorText(e);
  };
  const body =
    ask.challenge.reason === 'brake'
      ? t('mod.brake')
      : ask.challenge.reason === 'destructive'
        ? t('mod.brake.destructive')
        : useCode
          ? t('security.stepUp.codeBody')
          : t('security.stepUp.passwordBody');
  return (
    <Dialog
      title={useCode ? t('um.stepUp.title') : t('um.stepUp.titlePassword')}
      icon={ask.challenge.reason === 'account' ? 'shield' : 'alert'}
      tone={ask.challenge.reason === 'account' ? 'mode' : 'danger'}
      width={440}
      cls="ns-stepup"
      onClose={() => done(false)}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={() => done(false)}>
            {t('c.cancel')}
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            busy={busy}
            disabled={!value.trim()}
            onClick={() => void submit()}
          >
            {t('um.stepUp.continue')}
          </Btn>
        </>
      }
    >
      <p>{body}</p>
      {!useCode && ask.challenge.reason !== 'account' ? (
        <p className="ns-muted">{t('um.stepUp.passwordInstead')}</p>
      ) : null}
      <form
        className="ns-field"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label>
          <span className="ns-label">
            {useCode
              ? recovery
                ? t('um.recovery.codeLabel')
                : t('um.stepUp.code')
              : t('um.stepUp.password')}
          </span>
          <input
            className="nx-input"
            dir="ltr"
            type={useCode ? 'text' : 'password'}
            inputMode={useCode && !recovery ? 'numeric' : undefined}
            autoComplete={useCode ? 'one-time-code' : 'current-password'}
            value={value}
            data-autofocus
            aria-invalid={!!err || undefined}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>
      </form>
      {useCode ? (
        <button type="button" className="ns-link" onClick={() => setRecovery((v) => !v)}>
          {recovery ? t('auth.mfa.title') : t('um.stepUp.useRecovery')}
        </button>
      ) : null}
      <ErrorLine text={err} />
    </Dialog>
  );
}

/* ---------------- the host ---------------- */

export function ModHost() {
  const open = useModDialog((s) => s.open);
  const key = open ? `${open.kind}:${open.userId}` : '';
  let dlg = null;
  if (open) {
    const p = { guildId: open.guildId, userId: open.userId };
    if (open.kind === 'timeout') dlg = <TimeoutDialog key={key} {...p} />;
    else if (open.kind === 'kick') dlg = <KickDialog key={key} {...p} />;
    else if (open.kind === 'ban') dlg = <BanDialog key={key} {...p} />;
    else if (open.kind === 'nick') dlg = <NickDialog key={key} {...p} />;
    else if (open.kind === 'poke') dlg = <PokeDialog key={key} {...p} />;
    else if (open.kind === 'move') dlg = <MoveDialog key={key} {...p} />;
    else if (open.kind === 'transfer') dlg = <TransferDialog key={key} {...p} />;
  }
  return (
    <>
      {dlg}
      <StepUpDialog />
    </>
  );
}
